import { useState } from "react";
import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { canShowGeneratePaymentLink } from "../../events/shared/utils/eventRegistrationFormatters";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";

type PaymentMethod = "pix" | "credit_card" | "debit_card";

type AdminRegistrationRowActionsProps = {
  adminPaymentMethod: PaymentMethod;
  isAdmin: boolean;
  onCopyPaymentLink: (
    id: number,
    paymentMethod?: PaymentMethod,
    existingPaymentLink?: string | null,
  ) => void | Promise<void>;
  onDeleteRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onEditRegistration?: (registration: AdminEventRegistration) => void;
  onEditAvailabilityRestriction?: (registration: AdminEventRegistration) => void;
  onMarkPaidRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRejectRegistration?: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  registration: AdminEventRegistration;
  showCancelAction?: boolean;
  showEditAction?: boolean;
};

export default function AdminRegistrationRowActions({
  adminPaymentMethod,
  isAdmin,
  onCopyPaymentLink,
  onDeleteRegistration,
  onEditAvailabilityRestriction,
  onEditRegistration,
  onMarkPaidRegistration,
  onRejectRegistration,
  registration,
  showCancelAction = false,
  showEditAction = false,
}: AdminRegistrationRowActionsProps) {
  const t = siteTexts.admin;
  const [copyingPaymentLink, setCopyingPaymentLink] = useState(false);

  async function handleCopyPaymentLink() {
    if (copyingPaymentLink) return;
    setCopyingPaymentLink(true);
    try {
      await onCopyPaymentLink(
        registration.id,
        adminPaymentMethod,
        registration.paymentLink,
      );
    } finally {
      setCopyingPaymentLink(false);
    }
  }
  const canManageRestriction =
    Boolean(onEditAvailabilityRestriction) &&
    String(registration.challengeType || "").toLowerCase() === "tournament";

  return (
    <div className="challenge-registration-row-actions">
      {showEditAction && onEditRegistration && (
        <Button
          type="button"
          variant="pill"
          onClick={() => onEditRegistration(registration)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            edit
          </span>
          Editar
        </Button>
      )}

      {canManageRestriction && (
        <Button
          type="button"
          variant="pill"
          onClick={() => onEditAvailabilityRestriction?.(registration)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            event_busy
          </span>
          {registration.availabilityRestriction?.hasRestriction
            ? "Editar restrição"
            : "Adicionar restrição"}
        </Button>
      )}

      {canShowGeneratePaymentLink(registration) && (
        <Button
          type="button"
          variant="pill"
          disabled={copyingPaymentLink}
          onClick={() => void handleCopyPaymentLink()}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            content_copy
          </span>
          {copyingPaymentLink ? "Gerando..." : t.registrationsTab.copyLink}
        </Button>
      )}

      {isAdmin &&
        registration.paymentStatus !== "confirmed" &&
        registration.status !== "canceled" && (
          <Button
            type="button"
            variant="primary"
            onClick={() => onMarkPaidRegistration(registration.id, registration)}
          >
            {t.registrationsTab.confirmPayment}
          </Button>
        )}

      {showCancelAction &&
        onRejectRegistration &&
        registration.status !== "canceled" && (
          <Button
            type="button"
            variant="danger"
            onClick={() => onRejectRegistration(registration.id, registration)}
          >
            {siteTexts.common.cancel}
          </Button>
        )}

      <Button
        type="button"
        variant="danger"
        onClick={() => onDeleteRegistration(registration.id, registration)}
      >
        {siteTexts.common.remove}
      </Button>
    </div>
  );
}
